/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  GodavariAdDetail,
  GodavariAdDetailType,
  GodavariEventMetadata,
  GodavariHeartbeatFields,
  GodavariMetadata,
  GodavariPulseDetail,
  GodavariPulseStage,
  GodavariPulsePlayerState,
  GodavariVideoDetail,
  GodavariVideoDetailInput,
} from '../model/GodavariPayload';
import { getEventTimeMetadata, stringValue } from './GodavariPayloadBuilder';
import { GodavariConnector } from './GodavariSdk';

type HeartbeatSnapshot = () => GodavariHeartbeatFields;
type PlaybackPhase = 'startup' | 'playing' | 'ended';

/** Observation history belongs to one playback session; the SDK owns beacon delivery. */
export class GodavariPlaybackState {
  connector?: GodavariConnector;
  private playbackPhase: PlaybackPhase = 'startup';
  private activeAd = false;
  private adAttemptSent = false;
  private playerStateBeforeAd?: GodavariPulsePlayerState;
  private contentStarted = false;
  private attempted = false;
  private readonly pendingVideoDetails: GodavariVideoDetail[] = [];
  private readonly pendingPulseDetails: GodavariPulseDetail[] = [];
  private readonly pendingAdDetails: GodavariAdDetail[] = [];
  private readonly startupRequests = new Map<string, 0 | 1>();
  private readonly failedStartupRequests = new Set<string>();
  private firstPulseRequest?: string;
  private currentPlayerState: GodavariPulsePlayerState = 'Ready';
  private lastPulseStage?: GodavariPulseStage;
  private pendingSeek?: GodavariHeartbeatFields & {
    event_event_type: 'heart_beat_event_seek';
  };
  private droppedFrames = 0;
  private previousBitrate = 0;
  private previousAudioLanguage = 'und';
  private previousSubtitleLanguage = 'off';
  private videoSequence = 0;
  private pulseSequence = 0;

  get playerState(): GodavariPulsePlayerState {
    return this.currentPlayerState;
  }

  get latestPulseStage(): GodavariPulseStage | undefined {
    return this.lastPulseStage ? { ...this.lastPulseStage } : undefined;
  }

  setPlayerState(state: GodavariPulsePlayerState): void {
    if (this.phase !== 'ended') {
      this.currentPlayerState = state;
    }
  }

  get phase(): PlaybackPhase {
    return this.playbackPhase;
  }

  get adActive(): boolean {
    return this.activeAd;
  }

  /** The vendor derives the ad session id and start time (ads-sst) only from an ad attempt. */
  get adAttempted(): boolean {
    return this.adAttemptSent;
  }

  setAdAttempted(value: boolean): void {
    this.adAttemptSent = value;
  }

  get videoAttempted(): boolean {
    return this.attempted;
  }

  get hasPulseDetails(): boolean {
    return this.pendingPulseDetails.length > 0;
  }

  get hasAdDetails(): boolean {
    return this.pendingAdDetails.length > 0;
  }

  initializeHistory(metadata: GodavariMetadata): void {
    this.previousBitrate = Number(metadata.video_current_bitrate) || 0;
    this.previousAudioLanguage = stringValue(metadata.video_audio_language, 'und');
    this.previousSubtitleLanguage = stringValue(metadata.video_subtitle_language, 'off');
  }

  markPlaying(): void {
    this.contentStarted = true;
    this.playbackPhase = 'playing';
    this.currentPlayerState = 'Playing';
  }

  markEnded(): void {
    this.playbackPhase = 'ended';
  }

  restartStartupForRetry(): boolean {
    if (!this.connector || this.contentStarted || this.phase !== 'ended') {
      return false;
    }
    this.playbackPhase = 'startup';
    this.attempted = false;
    return true;
  }

  markVideoAttempted(): void {
    this.attempted = true;
  }

  recordVideoEvent(details: GodavariVideoDetailInput, snapshot: HeartbeatSnapshot): void {
    if (this.connector && this.phase !== 'ended') {
      this.pendingVideoDetails.push({ ...snapshot(), ...details });
    }
  }

  recordSeekStart(snapshot: HeartbeatSnapshot): void {
    if (this.connector && this.phase !== 'ended') {
      this.pendingSeek = {
        ...snapshot(),
        event_event_type: 'heart_beat_event_seek',
      };
    }
  }

  recordSeekEnd(snapshot: HeartbeatSnapshot): boolean {
    if (!this.pendingSeek || !this.connector || this.phase === 'ended') {
      return false;
    }
    this.pendingVideoDetails.push({
      ...this.pendingSeek,
      event_end_position: snapshot().event_start_position,
    });
    this.pendingSeek = undefined;
    return true;
  }

  recordBitrateChange(metadata: GodavariEventMetadata, snapshot: HeartbeatSnapshot): number | undefined {
    const from = metadata.event_from_bitrate ?? this.previousBitrate;
    const to = metadata.event_to_bitrate ?? metadata.video_current_bitrate;
    if (typeof to !== 'number' || !Number.isFinite(to) || typeof from !== 'number' || !Number.isFinite(from)) {
      return undefined;
    }
    if (to !== from) {
      this.recordVideoEvent(
        {
          event_event_type: to > from ? 'heart_beat_event_upshift' : 'heart_beat_event_downshift',
          event_from_bitrate: from,
          event_to_bitrate: to,
        },
        snapshot,
      );
    }
    this.previousBitrate = to;
    return to;
  }

  recordLanguageChange(kind: 'audio' | 'subtitle', next: unknown, snapshot: HeartbeatSnapshot): boolean {
    if (typeof next !== 'string') {
      return false;
    }
    const previous = kind === 'audio' ? this.previousAudioLanguage : this.previousSubtitleLanguage;
    if (previous !== next) {
      this.recordVideoEvent(
        kind === 'audio'
          ? {
              event_event_type: 'heart_beat_event_audio_language',
              from_audio_language: previous,
              to_audio_language: next,
            }
          : {
              event_event_type: 'heart_beat_event_subtitle_language',
              from_subtitle_language: previous,
              to_subtitle_language: next,
            },
        snapshot,
      );
    }
    if (kind === 'audio') {
      this.previousAudioLanguage = next;
    } else {
      this.previousSubtitleLanguage = next;
    }
    return true;
  }

  /** Returns true when a retry starts the first observed request again. */
  recordStartupStage(
    observation: GodavariPulseStage,
    timestamp: number,
    options: { requestId?: string; retryFromStart?: boolean } = {},
  ): boolean {
    if (!this.connector || this.phase !== 'startup' || !Number.isFinite(timestamp)) {
      return false;
    }
    const step = observation.pulse_start_steps;
    const requestKey = `${step}:${options.requestId ?? ''}`;
    const requestStart = observation.pulse_micro_steps === 'RequestStart';
    const canInferRetry = options.requestId !== undefined || this.failedStartupRequests.has(requestKey);
    const repeatedRequest = requestStart && canInferRetry && this.startupRequests.has(requestKey);
    let inferredRetry = this.startupRequests.get(requestKey) ?? 0;
    if (requestStart) inferredRetry = 0;
    if (repeatedRequest) inferredRetry = 1;
    const retry = options.retryFromStart === true ? 1 : (observation.pulse_player_retry ?? inferredRetry);
    const restarted =
      options.retryFromStart === true || (requestStart && retry === 1 && step === this.firstPulseRequest);
    if (restarted) {
      this.attempted = false;
      this.startupRequests.clear();
      this.failedStartupRequests.clear();
    }
    if (requestStart) {
      this.firstPulseRequest ??= step;
      this.startupRequests.set(requestKey, retry);
    }
    if (observation.pulse_micro_steps === 'ResponseError') this.failedStartupRequests.add(requestKey);
    if (observation.pulse_micro_steps === 'ResponseEnd') this.failedStartupRequests.delete(requestKey);
    this.currentPlayerState = observation.pulse_player_state;
    this.lastPulseStage = { ...observation, pulse_player_retry: retry };
    this.pendingPulseDetails.push({
      ...getEventTimeMetadata(timestamp),
      ...observation,
      event_event_type: 'pulse_event_name',
      pulse_player_retry: retry,
    });
    return restarted;
  }

  recordDroppedFrames(frames: unknown, snapshot: HeartbeatSnapshot): void {
    if (
      this.phase === 'playing' &&
      !this.adActive &&
      typeof frames === 'number' &&
      Number.isFinite(frames) &&
      frames >= 0
    ) {
      this.recordVideoEvent(
        {
          event_event_type: 'heart_beat_event_dropped_frames',
          event_increment_number: Math.max(0, Math.trunc(frames) - this.droppedFrames),
        },
        snapshot,
      );
      this.droppedFrames = Math.trunc(frames);
    }
  }

  beginAd(): boolean {
    if (!this.connector || this.adActive || this.phase === 'ended') {
      return false;
    }
    this.playerStateBeforeAd = this.currentPlayerState;
    this.currentPlayerState = 'Ad';
    this.activeAd = true;
    return true;
  }

  clearAdDetails(): void {
    this.pendingAdDetails.length = 0;
  }

  finishAd(): void {
    this.activeAd = false;
    if (this.phase !== 'ended') this.currentPlayerState = this.playerStateBeforeAd ?? 'Ready';
    this.playerStateBeforeAd = undefined;
  }

  /** 
   * records an ad event.
   * @param eventType the type of ad event being recorded.
   * @param snapshot a function that returns the current heartbeat snapshot.
   * @param force records events that happen outside an ad, such as a break skipped before any ad starts.
  */
  recordAdEvent(eventType: GodavariAdDetailType, snapshot: HeartbeatSnapshot, force = false): void {
    if (force || this.adActive) {
      this.pendingAdDetails.push({
        ...snapshot(),
        event_event_type: eventType,
      });
    }
  }

  takeVideoBatch(additionalDetails: GodavariVideoDetail[] = []): {
    video_sequence: number;
    event_data_list: GodavariVideoDetail[];
  } {
    this.pendingVideoDetails.push(...additionalDetails);
    if (this.pendingVideoDetails.length > 0) {
      this.videoSequence += 1;
    }
    return {
      video_sequence: this.videoSequence,
      event_data_list: this.pendingVideoDetails.splice(0),
    };
  }

  takePulseBatch(): {
    pulse_event_count_sequence: number;
    event_data_list: GodavariPulseDetail[];
  } {
    if (this.pendingPulseDetails.length > 0) {
      this.pulseSequence += 1;
    }
    return {
      pulse_event_count_sequence: this.pulseSequence,
      event_data_list: this.pendingPulseDetails.splice(0),
    };
  }

  takeAdDetails(): GodavariAdDetail[] {
    return this.pendingAdDetails.splice(0);
  }
}
